import React, {useMemo} from 'react';
import DashboardLayout from "../../../layouts/DashboardLayout";
import PageHeader from "../../../components/PageHeader";
import {Link, useParams} from 'react-router-dom';
import {useMutation, useQuery} from "react-query";
import StatusBadge from "../../../components/StatusBadge";
import ButtonLink from "../../../components/ButtonLink";
import Loading from "../../../components/Loading";
import {CANCEL_PAYMENT, GET_PAYMENT_TRANSACTIONS, GET_PAYMENT_WEBHOOKS} from "../../../utils/graphql";
import {apollo} from "../../../utils/apollo";
import InformationLine from "../../../components/InformationLine";
import ProductsDetails from "../../../components/ProductsDetails";
import MoneyHighlight from "../../../components/MoneyHighlight";
import {FlagIcon, CreditCardIcon, DocumentTextIcon, UserIcon, ClockIcon} from "@heroicons/react/24/outline";
import Button from "../../../components/Button";
import InvoiceField from "../../../components/InvoiceField";
import TransactionList from "../../../TransactionList";
import WebhookList from "../../../WebhookList";
import {useFetchPaymentQuery} from "../../../utils/queries";
import ButtonInternalLink from "../../../components/ButtonInternalLink";
import useModalStore from "../../../store/modal";
import QRCodePayment from "../../../components/QRCodePayment";
import {useTranslation} from "react-i18next";
import {toast} from "react-toastify";
import {clsx} from "clsx";

function PaymentView() {
    const {payment_uuid} = useParams();
    const {t} = useTranslation();
    const { setModal } = useModalStore();

    const fetchPaymentQuery = useFetchPaymentQuery(payment_uuid, {refetchInterval: 10000});

    const fetchPaymentTransactions = useQuery({
        queryKey: 'payment_transactions_view', queryFn: async () => {
            const {data} = await apollo.query({
                query: GET_PAYMENT_TRANSACTIONS, variables: {
                    paymentUuid: payment_uuid,
                },
            });
            return data?.getPaymentTransactions;
        }, keepPreviousData: false
    });

    const fetchPaymentWebhooks = useQuery({
        queryKey: ['payment_webhooks_view', payment_uuid],
        queryFn: async () => {
            const { data } = await apollo.query({
                query: GET_PAYMENT_WEBHOOKS,
                variables: { paymentUuid: payment_uuid },
            });
            return data?.getPaymentWebhooks;
        },
        keepPreviousData: false,
    });

    const cancelPaymentMutation = useMutation({
        mutationKey: 'cancel_payment', mutationFn: async () => {
            const {data} = await apollo.mutate({
                mutation: CANCEL_PAYMENT, variables: {
                    paymentUuid: payment_uuid
                },
            });
            return data?.cancelPayment;
        }, onSuccess: (canceled) => {
            if (canceled === true) {
                fetchPaymentQuery.refetch();
            }
        }
    })

    const payment = useMemo(() => fetchPaymentQuery?.isSuccess ? fetchPaymentQuery?.data : null, [fetchPaymentQuery]);
    const transactions = useMemo(() => fetchPaymentTransactions?.isSuccess ? fetchPaymentTransactions?.data : null, [fetchPaymentTransactions]);
    const webhooks = useMemo(() => fetchPaymentWebhooks?.isSuccess ? fetchPaymentWebhooks?.data : null, [fetchPaymentWebhooks]);

    return (<DashboardLayout>
        <PageHeader back="/merchant/payments" title="Payment Details"
                    description="View and manage payment information, customer details, and transaction history."/>

        {(fetchPaymentQuery.isLoading) && <Loading/>}
        {(fetchPaymentQuery.isSuccess && payment) && <div className="mt-8">
            <div className="mb-8 space-y-6">
                <div className="bg-white rounded-[2rem] p-8 shadow-sm border border-gray-100 flex flex-wrap items-center gap-8 justify-between">
                    <div className="flex items-center gap-6">
                        <div className={clsx(
                            "p-4 rounded-2xl",
                            payment?.status === 'completed' ? 'bg-green-600/10' : 
                            payment?.status === 'pending' ? 'bg-orange-600/10' : 
                            'bg-rose-600/10'
                        )}>
                            <CreditCardIcon className={clsx(
                                "h-8 w-8",
                                payment?.status === 'completed' ? 'text-green-600' : 
                                payment?.status === 'pending' ? 'text-orange-600' : 
                                'text-rose-600'
                            )} />
                        </div>
                        <div>
                            <div className="flex items-center gap-3 mb-1">
                                <h2 className="text-2xl font-black text-gray-900 tracking-tight">Payment {payment?.status.charAt(0).toUpperCase() + payment?.status.slice(1)}</h2>
                                <StatusBadge status={payment?.status} />
                            </div>
                            <p className="text-xs font-bold font-mono text-gray-400 uppercase tracking-widest">{payment?.paymentUuid}</p>
                        </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-10">
                        <div className="flex flex-col">
                            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Amount</span>
                            <MoneyHighlight value={payment?.amount} currency={payment?.currency?.symbol} />
                        </div>

                        <div className="flex flex-col">
                            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Environment</span>
                            {payment?.test ? 
                                <span className="inline-flex items-center px-3 py-1 rounded-full text-[10px] font-bold uppercase bg-rose-50 text-rose-600 border border-rose-100">Test Mode</span> : 
                                <span className="inline-flex items-center px-3 py-1 rounded-full text-[10px] font-bold uppercase bg-green-50 text-green-600 border border-green-100">Live Mode</span>
                            }
                        </div>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="lg:col-span-2 space-y-8">
                    <div className="bg-white rounded-[2rem] shadow-sm border border-gray-100 overflow-hidden">
                        <div className="px-8 py-6 border-b border-gray-50 flex items-center gap-3">
                            <div className="p-2.5 rounded-2xl bg-[#0b6a8c]/10">
                                <DocumentTextIcon className="h-6 w-6 text-[#0b6a8c]" />
                            </div>
                            <h3 className="text-lg font-bold text-gray-900">Payment Information</h3>
                        </div>
                        <div className="p-8">
                            <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-1">
                                <InformationLine title="Payment ID" value={payment?.paymentUuid}/>
                                <InformationLine title="Supplier" value={payment?.supplier?.supplierName}/>
                                <InformationLine title="Gateway Reference" value={payment?.gatewayReferenceNumber}/>
                                <InformationLine title="Customer Reference" value={payment?.customerReferenceNumber}/>
                                <InformationLine title="Broker Reference" value={payment?.brokerReference}/>
                                <InformationLine title={t('payment.create.business_line')} value={payment?.businessLine}/>
                                <InformationLine title="Auth Code" value={payment?.authCode}/>
                                <InformationLine title="Card Number" value={payment?.cardNumber}/>
                                <InformationLine title="Anti Fraud" value={payment?.antiFraud && <FlagIcon className="h-5 w-5 text-rose-500"/>}/>
                                <InformationLine title="Created At" value={payment?.createdAt}/>
                                <InformationLine title="Paid At" value={payment?.paidAt}/>
                                <InformationLine title="Refunded At" value={payment?.refundedAt}/>
                                <InformationLine title="Expires At" value={payment?.expiresAt}/>
                                <InformationLine title="Settlement ID" value={payment?.settlement?.settlementUuid}/>
                                <InformationLine title="Card Holder Name" value={payment?.cardHolderName}/>
                                <InformationLine title="IP Address" value={payment?.ipAddress}/>
                                <InformationLine title="International" value={payment?.isInternational ? "Yes" : "No"}/>
                                <InformationLine title="Split Payment" value={payment?.splitPayment ? "Yes" : "No"}/>
                                {/*<InformationLine title="Gateway Code" value={payment?.gatewayCode}/>*/}
                                <InformationLine title="Callback URL" value={payment?.callbackUrl}/>
                                <InformationLine title="Remarks" value={payment?.remarks}/>
                            </dl>
                        </div>
                    </div>

                    <ProductsDetails payment={payment}/>

                    {fetchPaymentTransactions.isLoading && <Loading/>}
                    {fetchPaymentTransactions.isSuccess &&
                        <TransactionList key={payment?.paymentUuid} transactions={transactions || []}/>}

                    {fetchPaymentWebhooks.isLoading && <Loading/>}
                    {fetchPaymentWebhooks.isSuccess &&
                        <WebhookList key={payment?.paymentUuid + '-wh'} webhooks={webhooks || []}/>}
                </div>

                <div className="space-y-8">
                    <div className="bg-white rounded-[2rem] shadow-sm border border-gray-100 overflow-hidden">
                        <div className="px-8 py-6 border-b border-gray-50 flex items-center gap-3">
                            <div className="p-2.5 rounded-2xl bg-[#0b6a8c]/10">
                                <UserIcon className="h-6 w-6 text-[#0b6a8c]" />
                            </div>
                            <h3 className="text-lg font-bold text-gray-900">Customer Details</h3>
                        </div>
                        <div className="p-8">
                            <div className="space-y-4">
                                <InvoiceField name="Name" value={payment?.name}
                                    className="col-span-12 text-sm leading-6 text-gray-700 p-4 bg-gray-50/50 border border-gray-100 rounded-2xl"/>
                                <InvoiceField name="Email" value={payment?.email}
                                    className="col-span-12 text-sm leading-6 text-gray-700 p-4 bg-gray-50/50 border border-gray-100 rounded-2xl"/>
                                <InvoiceField name="Phone" value={payment?.phone}
                                    className="col-span-12 text-sm leading-6 text-gray-700 p-4 bg-gray-50/50 border border-gray-100 rounded-2xl"/>
                                <InvoiceField name="Address" value={payment?.address}
                                    className="col-span-12 text-sm leading-6 text-gray-700 p-4 bg-gray-50/50 border border-gray-100 rounded-2xl"/>
                                <div className="grid grid-cols-2 gap-4">
                                    <InvoiceField name="City" value={payment?.city}
                                        className="col-span-1 text-sm leading-6 text-gray-700 p-4 bg-gray-50/50 border border-gray-100 rounded-2xl"/>
                                    <InvoiceField name="Zip Code" value={payment?.zipCode}
                                        className="col-span-1 text-sm leading-6 text-gray-700 p-4 bg-gray-50/50 border border-gray-100 rounded-2xl"/>
                                </div>
                                <InvoiceField name="Country" value={payment?.country}
                                    className="col-span-12 text-sm leading-6 text-gray-700 p-4 bg-gray-50/50 border border-gray-100 rounded-2xl"/>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-[2rem] shadow-sm border border-gray-100 overflow-hidden">
                        <div className="px-8 py-6 border-b border-gray-50 flex items-center gap-3">
                            <div className="p-2.5 rounded-2xl bg-[#0b6a8c]/10">
                                <ClockIcon className="h-6 w-6 text-[#0b6a8c]" />
                            </div>
                            <h3 className="text-lg font-bold text-gray-900">Payment Actions</h3>
                        </div>
                        <div className="p-8">
                            <div className="flex flex-col gap-4">
                                {['pending', 'failed'].includes(payment?.status) &&
                                    <Button
                                        className="w-full justify-center"
                                        onClick={() => {
                                            copyToClipboard(`${window.location.origin}/pay/${payment?.paymentUuid}`);
                                            toast.success('Copied to clipboard!')
                                        }}
                                    >
                                        Copy Link
                                    </Button>
                                }

                                {['pending', 'failed'].includes(payment?.status) &&
                                    <ButtonLink target="_blank" href={`/pay/${payment?.paymentUuid}`} className="w-full justify-center">
                                        Payment Link
                                    </ButtonLink>
                                }

                                {['pending', 'failed'].includes(payment?.status) &&
                                    <QRCodePayment 
                                        paymentUrl={`${window.location.origin}/pay/${payment?.paymentUuid}`}
                                        title={t('pay.share_payment_link')}
                                    />
                                }

                                {['pending', 'failed'].includes(payment?.status) &&
                                    <Button 
                                        secondary={true} 
                                        onClick={() => {
                                            setModal({
                                                isVisible: true,
                                                title: "Cancel Payment",
                                                description: "Are you sure you want to cancel this payment? This action cannot be undone.",
                                                confirmText: "Yes, Cancel Payment",
                                                onCancel: () => {

                                                },
                                                onConfirm: () => {
                                                    cancelPaymentMutation.mutate();
                                                }
                                            });
                                        }}
                                        disabled={cancelPaymentMutation.isLoading} 
                                        className="w-full justify-center"
                                    >
                                        Cancel Payment
                                    </Button>
                                }

                                {['completed'].includes(payment?.status) && payment?.isRefundAutomated && !payment?.refunds?.length &&
                                    <ButtonLink secondary={true} href={`/merchant/refunds/request/${payment?.paymentUuid}`} className="w-full justify-center">
                                        Refund Payment
                                    </ButtonLink>
                                }

                                {['completed'].includes(payment?.status) && payment?.isDuplicate &&
                                    <div className="p-4 bg-orange-50 border border-orange-100 rounded-2xl">
                                        <div className="flex gap-3">
                                            <FlagIcon className="h-5 w-5 text-orange-600 shrink-0" />
                                            <p className="text-xs font-medium text-orange-800 leading-relaxed">
                                                Refunds are disabled for this transaction as it is identified as a duplicate.
                                            </p>
                                        </div>
                                    </div>
                                }

                                {['completed'].includes(payment?.status) && payment?.callbackUrlAfterTransaction &&
                                    <ButtonLink href={payment?.callbackUrlAfterTransaction} target="_blank" className="w-full justify-center">
                                        Go to Callback URL
                                    </ButtonLink>
                                }

                                {!!payment?.refunds?.length &&
                                    <div className="mt-2">
                                        <div className="flex items-center gap-2 mb-2">
                                            <FlagIcon className="h-5 w-5 text-yellow-600"/>
                                            <span className="text-yellow-600 font-medium">Refund Information</span>
                                        </div>
                                        <div className="flex flex-col gap-2">
                                            {payment?.refunds?.map((refund, index) => (
                                                <Link key={index} to={`/merchant/refunds/view/${refund?.refundUuid}`}
                                                    className="bg-white border border-gray-100 text-gray-700 px-4 py-3 rounded-2xl shadow-sm text-[11px] font-bold uppercase tracking-wider hover:bg-gray-50 transition-colors flex justify-between items-center group">
                                                    <span>Refund: {refund?.refundUuid}</span>
                                                    <span className="text-[#0b6a8c] group-hover:translate-x-1 transition-transform">View →</span>
                                                </Link>
                                            ))}
                                        </div>
                                    </div>
                                }
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>}

    </DashboardLayout>);
}

function copyToClipboard(text) {
    // Create new element
    var el = document.createElement('textarea');
    // Set the value (string to be copied)
    el.value = text;
    // Set non-editable
    el.setAttribute('readonly', '');
    // Append the element to body
    document.body.appendChild(el);
    // The text is selected
    el.select();
    // Execute copy command
    document.execCommand('copy');
    // Remove the element
    document.body.removeChild(el);
}

export default PaymentView;
